// Candidate Screening Response viewing component
import {
  Box,
  Grid,
  Typography,
  Divider,
  Chip,
  Stack,
  LinearProgress,
  Skeleton,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  AIGradientIcon,
  SparkleContainer,
} from "../../../../Base/views/AIGradientIcon";
import { useEffect, useState } from "react";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { GetPreScreenTemplateResponseHelper } from "../../../../ATSSettings/helper/AtsFormTemplateResponseHelper";
import {
  getDomain,
  getImageSignedUrl,
} from "../../../../Base/helper/baseFunctions";
import { NoData } from "../../../../Base/views/NotAuthorized";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const PreScreenResponseViewer = ({ data, handleGenerateScreeningScore, useGeneratedScore }) => {
  const [response, setResponse] = useState({});
  const [viewMode, setViewMode] = useState("loading");
  const { AiConfigLoading, ModelTestingLoading } = useSelector(
    (state) => state.AncillaryReducer?.AIConfigReducer,
  );
  const { pythonScreeningLoading, pythonScreeningScore } = useSelector(
    (state) => state.AtsReducer?.AtsProfileScoreReducer,
  );
  const isScoreValid = useGeneratedScore ? data?.candidate_job_id &&
        pythonScreeningScore?.candidate_job_id && data?.candidate_job_id === pythonScreeningScore?.candidate_job_id : true;
  const jobScore = response?.ai_score?.job_score;
  const hasJobScore = jobScore !== null && jobScore !== undefined;
  const textScore = useGeneratedScore ? pythonScreeningScore?.scores?.text_score : data?.prescreen_textual_score;
  const videoScore = useGeneratedScore ? pythonScreeningScore?.scores?.video_score : data?.prescreen_video_score;
 
  useEffect(() => {
    if (!data?.f_prescreen_response_fk) {
      setViewMode("not_found");
      return;
    }
    GetPreScreenTemplateResponseHelper({
      workspace: getDomain(),
      prescreening_response_id: data?.f_prescreen_response_fk,
    }).then((res) => {
      if (res?.data?.statuscode === 200 && res?.data?.data) {
        setResponse(res?.data?.data?.[0] || {});
        setViewMode("response");
      } else {
        setViewMode("not_found");
      }
    });
  }, [data]);
  // Loading Messages during PreScreen Score Generation
  const aiMessages = [
    "Analyzing candidate responses...",
    "Evaluating communication clarity...",
    "Checking relevance to the question...",
    "Assessing explanation depth...",
    "Comparing answers with job requirements...",
    "Evaluating overall candidate suitability...",
    "Calculating final AI score..."
  ];
  const [messageIndex, setMessageIndex] = useState(0);
  useEffect(() => {
    if (!AiConfigLoading && !ModelTestingLoading && !pythonScreeningLoading) {
      return;
    }
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % aiMessages.length);
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AiConfigLoading || ModelTestingLoading || pythonScreeningLoading]);
  return (
    <Box>
      <Divider sx={{ mt: 3 }} />
      {viewMode === "loading" && <LoadingSkeleton />}

      {viewMode === "not_found" && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            Candidate Screening Response
          </Typography>
          <NoData title="No Response Found" />
        </Box>
      )}
      {viewMode === "response" && (
        <Box sx={{ p: 3, pb: 1, pr: 0 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={9}>
              {/* HEADER */}

              <Stack spacing={1} mb={3}>
                <Typography variant="h5" fontWeight={700}>
                  Candidate Screening Response
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Review comprehensive assessment results and video responses
                </Typography>
              </Stack>

              {/* PRESCREEN */}

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: "2px solid #E5E7EB",
                  boxShadow: "none",
                  backgroundColor: "#ffffff",
                  borderRadius: "10px",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <DescriptionOutlinedIcon color="primary" />

                  <Typography variant="h6" fontWeight={600}>
                    PreScreening Assessment
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {Object.entries(response?.prescreen_response || {})?.map(
                  ([key, value], i) => (
                    <QuestionItem
                      key={key}
                      index={i + 1}
                      title={formattedKey(key)}
                      answer={value}
                    />
                  ),
                )}
              </Box>

              {/* VIDEO SECTION */}

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: "2px solid #E5E7EB",
                  boxShadow: "none",
                  backgroundColor: "#ffffff",
                  borderRadius: "10px",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <VideocamOutlinedIcon color="primary" />

                  <Typography variant="h6" fontWeight={600}>
                    Video Assessment
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {response?.video_prescreen_response?.map((video, i) => (
                  <VideoQuestion
                    key={video.id}
                    index={i + 1}
                    question={video.question}
                    video={video.answer}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  borderRadius: 3,
                  p: 2,
                  position: "sticky",
                  top: 10,
                  border: "2px solid #E5E7EB",
                  backgroundColor: "#ffffff",
                }}
              >
                <Stack spacing={3}>
                  {/* Header */}
                  <Box sx={{ position: "relative" }}>
                    <Typography variant="h4" mb={2}>
                      AI Score
                    </Typography>
                    <Box
                      sx={{ position: "absolute", top: "-7px", left: "96px" }}
                    >
                      <SparkleContainer>
                        <AIGradientIcon className="sparkle-main" size={24} />
                      </SparkleContainer>
                    </Box>
                  </Box>
                {AiConfigLoading || ModelTestingLoading || pythonScreeningLoading ? (
                  <>
                   <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <CircularProgress size={16} />
                      <Typography fontSize={12} color="text.secondary">
                        {aiMessages[messageIndex]}
                      </Typography>
                    </Stack>
                    <Skeleton height={20} width="80%" />
                    <Skeleton variant="rectangular" height={6} />
                    <Skeleton height={20} width="60%" />

                    <Skeleton height={20} width="80%" />
                    <Skeleton variant="rectangular" height={6} />
                    <Skeleton height={20} width="60%" />
                  </>
                  ) : isScoreValid ? (
                <>
                  {/* Prescreen */}
                  <Box>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={14}>Pre-Screening Score</Typography>
                      <Typography fontWeight={700}>
                        {textScore ? `${textScore}/10` : "N/A"}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={textScore ? textScore * 10 : 0}
                      sx={{
                        height: 6,
                        borderRadius: 5,
                        mt: 1,
                        backgroundColor: "#f1f1f1",
                        "& .MuiLinearProgress-bar": {
                           backgroundColor: textScore ? getScoreColor(textScore) : "#d1d5db",
                        },
                      }}
                    />

                    {textScore && <Typography
                      fontSize={12}
                      mt={0.5}
                      sx={{
                        color: getScoreColor(textScore),
                      }}
                    >
                      {getScoreLabel(textScore)}
                    </Typography>}
                  </Box>

                  {/* Video */}
                  <Box>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontSize={14}>Pre-Screening Video Score</Typography>
                      <Typography fontWeight={700}>
                        {videoScore ? `${videoScore}/10` : "N/A"}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={videoScore ? videoScore * 10 : 0}
                      sx={{
                        height: 6,
                        borderRadius: 5,
                        mt: 1,
                        backgroundColor: "#f1f1f1",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: videoScore ? getScoreColor(videoScore) : "#d1d5db",
                        },
                      }}
                    />

                    {videoScore && <Typography
                      fontSize={12}
                      mt={0.5}
                      sx={{
                        color: getScoreColor(videoScore),
                      }}
                    >
                      {getScoreLabel(videoScore)}
                    </Typography>}
                  </Box>
                </>)
                : (
                  <Box textAlign="center" py={2}>
                    <Typography fontSize={13} color="text.secondary" mb={2}>
                      AI score not generated yet
                    </Typography>

                    <Button
                      variant="contained"
                      size="small"
                      className="ai-mpl-btn"
                      startIcon={<AutoAwesomeIcon sx={{color: "#ffff"}} size={16} />}
                      onClick={handleGenerateScreeningScore}
                    >
                      Generate Score
                    </Button>
                  </Box>
                )}
                <Box>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontSize={14}>Job Score</Typography>
                    <Typography fontWeight={700}>
                      {hasJobScore ? `${jobScore}/10` : "N/A"}
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={hasJobScore ? jobScore * 10 : 0}
                    sx={{
                      height: 6,
                      borderRadius: 5,
                      mt: 1,
                      backgroundColor: "#f1f1f1",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: hasJobScore ? getScoreColor(jobScore) : "#d1d5db",
                      },
                    }}
                  />

                  {hasJobScore && <Typography
                    fontSize={12}
                    mt={0.5}
                    sx={{
                      color: getScoreColor(response?.ai_score?.job_score),
                    }}
                  >
                    {getScoreLabel(response?.ai_score?.job_score)}
                  </Typography>}
                </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

const MAX_LENGTH = 200;

const TruncatedText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return "Not answered";

  const isLong = text.length > MAX_LENGTH;
  const display = expanded
    ? text
    : text.slice(0, MAX_LENGTH) + (isLong ? "..." : "");

  return (
    <Typography variant="body2" color="text.secondary">
      {display}
      {isLong && (
        <span
          style={{
            color: "#1976d2",
            cursor: "pointer",
            marginLeft: 6,
            fontWeight: 500,
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : "Show more"}
        </span>
      )}
    </Typography>
  );
};

const QuestionItem = ({ index, title, answer }) => (
  <Box
    sx={{
      border: "1px solid #E5E7EB",
      borderRadius: 2,
      p: 2,
      mb: 2,
      background: "#FAFAFA",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
      <Chip
        label={`Q${index}`}
        size="small"
        sx={{
          background: "#1976d2",
          color: "#fff",
          fontWeight: 600,
        }}
      />

      <Typography fontWeight={600}>{title}</Typography>
    </Stack>

    {/* RESPONSE */}
    {answer?.type === "file" ? (
      answer?.value ? (
        <Box display="flex" alignItems="center" gap="12px">
          <Typography
            sx={{
              color: "primary.main",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() =>
              handleView(answer?.value.replace(/"/g, ""), "download", title)
            }
          >
            Download Attachment
          </Typography>

          <IconButton
            size="small"
            onClick={() =>
              handleView(answer?.value.replace(/"/g, ""), "preview")
            }
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Not uploaded
        </Typography>
      )
    ) : answer?.type === "radio" ? (
      <TruncatedText text={formatRadioValue(answer?.value)} />
    ) : (
      <TruncatedText text={answer?.value} />
    )}
  </Box>
);

const VideoQuestion = ({ index, question, video }) => {
  const [videoUrl, setVideoUrl] = useState("");

  useEffect(() => {
    const loadVideo = async () => {
      if (video && video !== "Skipped") {
        const url = await getImageSignedUrl(video);
        setVideoUrl(url);
      }
    };

    loadVideo();
  }, [video]);
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 2,
        p: 2,
        mb: 2,
        background: "#FAFAFA",
      }}
    >
      <Stack direction="row" alignItems="start" spacing={1} mb={1}>
        <Chip
          label={`Q${index}`}
          size="small"
          sx={{
            background: "#FCE4EC",
            color: "#C2185B",
            fontWeight: 600,
          }}
        />
        <Typography variant="body2" fontWeight={500}>
          {question}
        </Typography>

        {video !== "Skipped" ? (
          <CheckCircleOutlineIcon
            sx={{ color: "#22c55e", fontSize: 18, ml: "auto" }}
          />
        ) : (
          <CancelOutlinedIcon
            sx={{ color: "#ef4444", fontSize: 18, ml: "auto" }}
          />
        )}
      </Stack>

      {video !== "Skipped" ? (
        videoUrl ? (
          <video
            controls
            width="100%"
            src={videoUrl}
            style={{
              borderRadius: 8,
              background: "#000",
            }}
          />
        ) : (
          <Skeleton variant="rectangular" height={200} />
        )
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          Not answered
        </Typography>
      )}
    </Box>
  );
};
const getScoreColor = (score) => {
  if (score >= 7) return "#5cd737"; // green
  if (score >= 4) return "#fecb03"; // yellow
  return "#e57373"; // red
};


const getScoreLabel = (score) => {
  if (score >= 7) return "Excellent";
  if (score >= 4) return "Average";
  return "Poor";
};
const formattedKey = (key) =>
  key
    ?.replace(/_/g, " ")
    ?.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
const formatRadioValue = (value) => {
  if (!value) return "";
  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
const handleView = async (filename, actionType = "preview", key) => {
  try {
    const url = await getImageSignedUrl(filename);
    if (actionType === "download") {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = key || filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } else if (actionType === "preview") {
      if ([".doc", ".docx"].some((ext) => filename.endsWith(ext))) {
        window.open(
          `https://docs.google.com/viewer?url=${encodeURIComponent(
            url,
          )}&embedded=true`,
          "_blank",
        );
      } else if ([".xls", ".xlsx"].some((ext) => filename.endsWith(ext))) {
        window.open(
          `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`,
          "_blank",
        );
      } else {
        window.open(url, "_blank");
      }
    }
    return url;
  } catch (err) {
    toast.error(err?.message || "Something went wrong");
  }
};
const LoadingSkeleton = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="text" width={300} height={40} />

    <Box mt={3}>
      {[1, 2].map((i) => (
        <Box key={i} mb={2}>
          <Skeleton
            variant="rectangular"
            height={90}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      ))}
    </Box>

    <Box mt={3}>
      {[1, 2].map((i) => (
        <Box key={i} mb={2}>
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      ))}
    </Box>
  </Box>
);

export default PreScreenResponseViewer;
